import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppShell from '../layout/AppShell';
import Help from './Help';
import DashboardPage from '../pages/DashboardPage';
import SettingsPage from '../pages/settings/SettingsPage';
import QuotesListPage from '../pages/quotes/QuotesListPage';
import CreateQuotePage from '../pages/quotes/CreateQuotePage';
import QuoteDetailPage from '../pages/quotes/QuoteDetailPage';
import EditQuotePage from '../pages/quotes/EditQuotePage';
import AutomaticDiscountsPage from '../pages/tools/AutomaticDiscountsPage';
import HideProductsPage from '../pages/tools/HideProductsPage';
import PaymentsPage from '../pages/account/PaymentsPage';
import QuoteChatPage from '../pages/chat/QuoteChatPage';
import InstallationPage from '../pages/settings/InstallationPage';
import CompaniesPage from '../pages/business/CompaniesPage';
import CustomersPage from '../pages/business/CustomersPage';
import CustomerDetailPage from '../pages/business/CustomerDetailPage';
import CustomerGroupsPage from '../pages/business/CustomerGroupsPage';
import { getStoreId, getSettings } from '../utils/storage';
import { api } from '../utils/apiClient';
import { parseRoute, navigate } from '../utils/navigation';
import { isQuoteChatUiEnabled } from '../utils/featureFlags';
import * as logger from '../utils/logger';
import './MainApp.css';

const MainApp = ({ storeInfo }) => {
  const { t } = useTranslation();
  const [route, setRoute] = useState(() => parseRoute());
  const [settings, setSettings] = useState(null);
  const [account, setAccount] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [hasAutoConnected, setHasAutoConnected] = useState(false);

  useEffect(() => {
    const onHashChange = () => setRoute(parseRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    if (typeof window.EcwidApp === 'undefined' || window.self === window.top) return;
    // One-shot baseline; autoheight owns ongoing resize (do not re-set on every route)
    window.EcwidApp.setSize({ height: 1400 });
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      const storeId = getStoreId();
      if (!storeId) {
        setIsLoadingSettings(false);
        setSettings({});
        return;
      }

      const stored = getSettings();
      if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
        setSettings(stored);
        setIsLoadingSettings(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_BACKEND_URL;
      try {
        // Local BFF settings include apiKey from register→connect
        const response = await fetch(`${apiUrl}/api/settings?id=${storeId}`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data.success && data.settings ? data.settings : {});
        } else {
          setSettings({});
        }
      } catch (error) {
        logger.error('MainApp: Failed to load settings:', error);
        setSettings({});
      } finally {
        setIsLoadingSettings(false);
      }
    };

    loadSettings();
  }, []);

  // Live trial / subscription from platform API (BFF settings do not include these).
  useEffect(() => {
    if (isLoadingSettings || !settings?.apiKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getSettingsSection('account');
        if (!cancelled) setAccount(data?.account || null);
      } catch (err) {
        logger.error('MainApp: Failed to load account:', err);
        if (!cancelled) setAccount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoadingSettings, settings?.apiKey]);

  useEffect(() => {
    if (!isLoadingSettings && settings && !settings.apiKey && !hasAutoConnected) {
      setHasAutoConnected(true);
      const storeId = getStoreId();
      if (!storeId) {
        logger.warn('MainApp: auto-register skipped, no store id');
        setHasAutoConnected(false);
        return;
      }

      logger.info('MainApp: auto-register start', storeId);
      setTimeout(() => {
        api
          .register()
          .then((data) => {
            if (data.success || data.api_key) {
              logger.info('MainApp: auto-register done', storeId);
              window.location.reload();
            } else {
              logger.warn('MainApp: auto-register returned no api key', storeId);
              setHasAutoConnected(false);
            }
          })
          .catch((err) => {
            logger.error('MainApp: Auto-connect failed:', err);
            setHasAutoConnected(false);
          });
      }, 100);
    }
  }, [settings, isLoadingSettings, hasAutoConnected]);

  const handleNavigate = useCallback((path) => {
    navigate(path);
  }, []);

  const storeCurrency = (
    String(storeInfo?.formatsAndUnits?.currency || settings?.currency || 'USD')
      .trim()
      .toUpperCase() || 'USD'
  );

  const showContentLoading = isLoadingSettings || (hasAutoConnected && !settings?.apiKey);

  const renderContent = () => {
    if (showContentLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="text-center">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
              <span className="visually-hidden">{t('mainApp.loading')}</span>
            </div>
            <p className="text-muted small mb-0">
              {hasAutoConnected ? t('mainApp.connectingAccount') : t('mainApp.loadingSettings')}
            </p>
          </div>
        </div>
      );
    }

    switch (route.name) {
      case 'help':
        return <Help />;
      case 'quotes':
        return <QuotesListPage onNavigate={handleNavigate} storeCurrency={storeCurrency} />;
      case 'quotes-create':
        return (
          <CreateQuotePage
            onNavigate={handleNavigate}
            customerId={route.query?.customerId || ''}
            storeCurrency={storeCurrency}
          />
        );      case 'quotes-detail':
        return <QuoteDetailPage quoteId={route.params.id} onNavigate={handleNavigate} storeCurrency={storeCurrency} />;
      case 'quotes-edit':
        return <EditQuotePage quoteId={route.params.id} onNavigate={handleNavigate} storeCurrency={storeCurrency} />;
      case 'settings':
        return <SettingsPage section={route.section} onNavigate={handleNavigate} />;
      case 'installation':
        return <InstallationPage />;
      case 'companies':
        return <CompaniesPage storeCurrency={storeCurrency} />;
      case 'customers':
        return <CustomersPage onNavigate={handleNavigate} storeCurrency={storeCurrency} />;
      case 'customers-detail':
        return (
          <CustomerDetailPage
            customerId={route.params.id}
            onNavigate={handleNavigate}
            storeCurrency={storeCurrency}
          />
        );
      case 'customer-groups':
        return <CustomerGroupsPage />;
      case 'tools-discounts':
        return <AutomaticDiscountsPage storeCurrency={storeCurrency} />;
      case 'tools-hide-products':
        return <HideProductsPage key="quote" />;
      case 'tools-hide-add-to-cart':
        return <HideProductsPage key="cart" mode="cart" />;
      case 'account-payments':
        return <PaymentsPage />;
      case 'chat':
        if (!isQuoteChatUiEnabled()) {
          return <DashboardPage onNavigate={handleNavigate} storeCurrency={storeCurrency} />;
        }
        return (
          <QuoteChatPage
            onNavigate={handleNavigate}
            initialThreadId={route.query?.thread || ''}
            quoteChatEnabled={settings?.quoteChatEnabled !== false}
          />
        );
      case 'dashboard':
      default:
        return <DashboardPage onNavigate={handleNavigate} storeCurrency={storeCurrency} />;
    }
  };

  const shellSettings = {
    ...(settings || {}),
    ...(account || {}),
  };

  return (
    <AppShell storeInfo={storeInfo} settings={shellSettings} route={route} onNavigate={handleNavigate}>
      {renderContent()}
    </AppShell>
  );
};

export default MainApp;
